/**
 * Additional validation checks for similarity matching
 * These heuristics help reduce false positives from numeric similarity scores
 */

import { stripVariantSuffix } from "./trackVariantDetection";

/**
 * Check if two titles are too different in length to be variants
 * A huge length difference usually indicates different songs
 * Note: We strip variant suffixes first to avoid rejecting valid matches like
 * "Song Title" vs "Song Title - Live at Madison Square Garden"
 */
export function hasSuspiciousLengthDifference(title1: string, title2: string): boolean {
  // Strip variant suffixes to get base titles for comparison
  const base1 = stripVariantSuffix(title1).trim();
  const base2 = stripVariantSuffix(title2).trim();

  const len1 = base1.length;
  const len2 = base2.length;
  const longer = Math.max(len1, len2);
  const shorter = Math.min(len1, len2);

  // If one title is less than 50% the length of the other, likely different songs
  // Example: "I Told You Things" (18 chars) vs "I Told You That I Was Afraid" (29 chars)
  // Ratio: 18/29 = 0.62 (above threshold, would pass)

  // If one title is less than 40% the length of the other, very likely different songs
  const ratio = shorter / longer;
  return ratio < 0.4;
}

/**
 * Check if the unique words are too different
 * Two songs might share common words but have different meanings
 */
export function hasInsufficientUniqueWordOverlap(title1: string, title2: string): boolean {
  // Normalize and split into words
  const words1 = title1.toLowerCase().trim().split(/\s+/).filter(w => w.length > 2);
  const words2 = title2.toLowerCase().trim().split(/\s+/).filter(w => w.length > 2);

  if (words1.length === 0 || words2.length === 0) {
    return true;
  }

  // Count how many words overlap
  const set1 = new Set(words1);
  const set2 = new Set(words2);
  const intersection = new Set([...set1].filter(word => set2.has(word)));

  // Calculate overlap percentage for both titles
  const overlap1 = intersection.size / words1.length;
  const overlap2 = intersection.size / words2.length;

  // If less than 50% of words overlap in EITHER title, likely different songs
  // Example: "I Told You Things" -> ["told", "you", "things"]
  //          "I Told You That I Was Afraid" -> ["told", "you", "that", "was", "afraid"]
  //          Overlap: ["told", "you"] = 2 words
  //          overlap1: 2/3 = 0.67 (would pass)
  //          overlap2: 2/5 = 0.40 (would fail)
  return Math.min(overlap1, overlap2) < 0.5;
}

/**
 * Validate if a similarity match is likely a true positive
 * Returns true if the match passes validation, false if it's likely a false positive
 */
export function validateSimilarityMatch(
  title1: string,
  title2: string,
  similarityScore: number,
): { isValid: boolean; reason?: string } {
  // Very high similarity scores are usually reliable
  if (similarityScore >= 0.9) {
    return { isValid: true };
  }

  // Check for suspicious length differences
  if (hasSuspiciousLengthDifference(title1, title2)) {
    return {
      isValid: false,
      reason: "Titles have very different lengths",
    };
  }

  // Check for insufficient word overlap
  if (hasInsufficientUniqueWordOverlap(title1, title2)) {
    return {
      isValid: false,
      reason: "Titles share too few unique words",
    };
  }

  return { isValid: true };
}
